import React, {FC} from 'react';

import {Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {EventHandler, Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {getColorHighlight} from '../../../../../../helpers/models/highlightHelpers';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {TagModel} from '../../../../../../models/api/tagModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {SettingsTagTabsEnum} from '../../../../../../models/url/settingsUrlModel';
import {makeSettingsUrlFromPathId} from '../../../../../../settings/menu/settingsHelpers';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {CreateTagRuleDropdownContainer} from '../../../../../common/dropdowns/rules/createTagRuleDropdownContainer';
import {EditTagDropdownContainer} from '../../../../../common/dropdowns/tags/editTagDropdownContainer';
import {NavigateToLinkMethod, useLinkNavigation} from '../../../../../common/navigation/useLinkNavigation';
import {BulkActionConfirmationDialog} from '../../actions/bulk/bulkActionConfirmationDialog';
import {ConversationHeaderPill, StyledPopoverCoordinatorWrapperDiv} from '../conversationHeaderPill';
import {ConversationHeaderTagPillContent} from './conversationHeaderTagPillContent';
import {ConversationHeaderTagPillTooltip} from './conversationHeaderTagPillTooltip';
import {TagDropdownContainer} from './tagDropdownContainer';

/*
 * Props.
 */

export interface ConversationHeaderTagPillProps {
  panelId: ViewerPanelId;
  userId: number;
  teammate: TeammateModel;
  tag: TagModel;
  tagsPath: ReadonlyArray<TagModel>;
  isLastPrivateTag: boolean;
  teammates: ReadonlyArray<TeammateModel>;
  hasPermission: (namespace: string, permission: PermissionsEnum) => boolean;
  isBulkActionRequired: boolean;
  onUntag: Handler;
}

/*
 * Component.
 */

export const ConversationHeaderTagPill: FC<ConversationHeaderTagPillProps> = (props) => {
  const [isEditOpen, toggleIsEditOpen] = useToggle();
  const [isCreateRuleOpen, toggleIsCreateRuleOpen] = useToggle();
  const [isBulkConfirmOpen, toggleIsBulkConfirmOpen] = useToggle();
  const {navigateToLink} = useLinkNavigation();

  return (
    <StyledPopoverCoordinatorWrapperDiv>
      <PopoverCoordinator
        interactionId="tag-header-pill"
        renderButton={(isOpen) => renderPill(isOpen, props)}
        renderModal={(popoverProps) =>
          renderDropdown(
            popoverProps,
            props,
            isEditOpen,
            toggleIsEditOpen,
            isCreateRuleOpen,
            toggleIsCreateRuleOpen,
            isBulkConfirmOpen,
            toggleIsBulkConfirmOpen,
            navigateToLink,
          )
        }
      />
    </StyledPopoverCoordinatorWrapperDiv>
  );
};

/*
 * Dropdown.
 */

function renderDropdown(
  popoverProps: PopoverTransferProps,
  props: ConversationHeaderTagPillProps,
  isEditOpen: boolean,
  toggleIsEditOpen: Handler,
  isBulkConfirmOpen: boolean,
  toggleIsBulkConfirmOpen: Handler,
  isCreateRuleOpen: boolean,
  toggleIsCreateRuleOpen: Handler,
  navigateToLink: NavigateToLinkMethod,
) {
  if (isCreateRuleOpen) {
    return renderCreateRuleDialog(popoverProps, props, toggleIsCreateRuleOpen);
  }

  if (isEditOpen) {
    return renderEditDialog(popoverProps, props, toggleIsEditOpen);
  }

  if (isBulkConfirmOpen) {
    return renderBulkConfirmDialog(popoverProps, props, toggleIsBulkConfirmOpen);
  }

  return renderListDropdown(
    popoverProps,
    props,
    toggleIsEditOpen,
    toggleIsCreateRuleOpen,
    toggleIsBulkConfirmOpen,
    navigateToLink,
  );
}

/*
 * Pill.
 */

function renderPill(isOpen: boolean, props: ConversationHeaderTagPillProps) {
  const {teammate, tag, tagsPath} = props;

  // Special appearance for the Starred tag when displayed.
  if (tag.id === teammate.starredTagId) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <ConversationHeaderPill
            hasHover={isOpen}
            isPrivate
            renderIcon={() => <Icon name="starFilledSmall" color={theme.palette.yellow.shade40} />}
          >
            <Trans id="tags-starredtag">Star</Trans>
          </ConversationHeaderPill>
        )}
      </UserThemeConsumer>
    );
  }

  // Special appearance for the Inbox tag.
  if (tag.isMainInboxTag) {
    return (
      <ConversationHeaderPill hasHover={isOpen} isPrivate renderIcon={() => <Icon name="inboxFilledSmall" />}>
        <Trans id="tags-inboxtag">Inbox</Trans>
      </ConversationHeaderPill>
    );
  }

  const color = getColorHighlight(tag);
  const isPrivate = tag.namespace === teammate.namespace;

  // Normal case for any other tag.
  return (
    <ConversationHeaderPill
      hasHover={isOpen}
      color={color}
      isPrivate={isPrivate}
      renderTooltip={() => !isOpen && renderPillTooltip(tagsPath)}
    >
      <ConversationHeaderTagPillContent tagsPath={tagsPath} />
    </ConversationHeaderPill>
  );
}

function renderPillTooltip(tagsPath: ReadonlyArray<TagModel>) {
  return <ConversationHeaderTagPillTooltip tagsPath={tagsPath} />;
}

/*
 * Tag details dropdown.
 */

function renderListDropdown(
  popoverProps: PopoverTransferProps,
  props: ConversationHeaderTagPillProps,
  toggleIsEditOpen: Handler,
  toggleIsCreateRuleOpen: Handler,
  toggleIsBulkConfirmOpen: Handler,
  navigateToLink: NavigateToLinkMethod,
) {
  const onEditClick: EventHandler = (e) => {
    e.preventDefault(); // Keep the dropdown open
    if (props.tag.archivedAt) {
      const tagUrl = makeSettingsUrlFromPathId(
        'settings.new-tags',
        {
          namespace: props.tag.namespace,
        },
        {
          page: SettingsTagTabsEnum.ARCHIVED,
          search: props.tag.name,
        },
      );
      navigateToLink(tagUrl);
    } else {
      toggleIsEditOpen(); // Switch to Edit Tag
    }
  };

  const onCreateRuleClick: EventHandler = (e) => {
    e.preventDefault(); // Keep the dropdown open
    toggleIsCreateRuleOpen(); // Switch to Create Rule
  };

  const onUntagClick: EventHandler = (e) => {
    if (props.isBulkActionRequired) {
      e.preventDefault();
      toggleIsBulkConfirmOpen();
      return;
    }

    props.onUntag();
  };

  return (
    <TagDropdownContainer
      {...popoverProps}
      teammate={props.teammate}
      tag={props.tag}
      isLastPrivateTag={props.isLastPrivateTag}
      onEditClick={onEditClick}
      onCreateRuleClick={onCreateRuleClick}
      onUntagClick={onUntagClick}
    />
  );
}

/*
 * Tag edit dialog.
 */

function renderEditDialog(
  popoverProps: PopoverTransferProps,
  props: ConversationHeaderTagPillProps,
  toggleIsEditOpen: Handler,
) {
  const onRequestClose = () => {
    popoverProps.onRequestClose(); // Close the dropdown
    toggleIsEditOpen(); // Reset back to List
  };

  return (
    <EditTagDropdownContainer
      {...props}
      {...popoverProps}
      onRequestClose={onRequestClose}
      onBackButtonClick={toggleIsEditOpen}
    />
  );
}

function renderBulkConfirmDialog(
  popoverProps: PopoverTransferProps,
  props: ConversationHeaderTagPillProps,
  toggleIsBulkConfirmOpen: Handler,
) {
  const onRequestClose = () => {
    popoverProps.onRequestClose();
    toggleIsBulkConfirmOpen();
  };
  return (
    <BulkActionConfirmationDialog
      onConfirmClick={props.onUntag}
      onRequestClose={onRequestClose}
      actionType={BulkActionTypesEnum.UNTAG}
    />
  );
}

/*
 * Create rule dialog.
 */

function renderCreateRuleDialog(
  popoverProps: PopoverTransferProps,
  props: ConversationHeaderTagPillProps,
  toggleIsCreateRuleOpen: Handler,
) {
  const onRequestClose = () => {
    popoverProps.onRequestClose(); // Close the dropdown
    toggleIsCreateRuleOpen(); // Reset back to List
  };
  return <CreateTagRuleDropdownContainer {...props} {...popoverProps} onRequestClose={onRequestClose} />;
}
